/**
 * StaffHomePage — Home page personale per ruoli `store_user` + `store_manager`.
 *
 * Mostra in una vista aggregata:
 *  - Hero greeting con foto/nome/mansione/negozio
 *  - Performance ultimo mese (6 KPI tiles con delta vs mese precedente)
 *  - Documenti in scadenza ≤30gg (banner ambra)
 *  - Ticket aperti su di te (assegnati o creati)
 *  - Agenda della settimana (compleanni + anniversari colleghi)
 *  - Achievement recenti
 *
 * Endpoint backend: GET /api/staff/me/dashboard
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO, differenceInDays } from 'date-fns';
import { it as itLocale } from 'date-fns/locale';
import {
  Euro, TrendingUp, Users as UsersIcon, Clock,
  FileWarning, Wrench, Cake, PartyPopper, Award, ChevronRight, AlertTriangle,
} from 'lucide-react';
import { staffApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';

const ROLE_LABELS = {
  barber_junior: 'Barbiere junior', barber: 'Barbiere', barber_senior: 'Barbiere senior',
  hair_stylist: 'Hair stylist', store_manager: 'Store manager',
  receptionist: 'Receptionist', apprentice: 'Apprendista',
  area_manager: 'Area manager', operations_manager: 'Operations manager',
  other: 'Altro',
};

const PRIORITY_LABELS = {
  low: 'Bassa', normal: 'Normale', high: 'Alta', urgent: 'Urgente',
};
const PRIORITY_TONES = {
  low: 'bg-slate-100 text-slate-700',
  normal: 'bg-blue-50 text-blue-700',
  high: 'bg-amber-100 text-amber-800',
  urgent: 'bg-rose-100 text-rose-800',
};
const STATUS_LABELS = {
  open: 'Aperto', in_progress: 'In lavorazione',
  waiting_parts: 'Attesa ricambi', resolved: 'Risolto', closed: 'Chiuso',
};

const DOC_TYPE_LABELS = {
  contract: 'Contratto', haccp: 'HACCP', medical_visit: 'Visita medica',
  training: 'Formazione', id_document: 'Documento identità', other: 'Altro',
};

const KEY_METRICS = [
  { key: 'total_sales', label: 'Vendite totali', icon: Euro, format: 'currency', tone: 'emerald' },
  { key: 'services_sold', label: 'Servizi venduti', icon: TrendingUp, format: 'int', tone: 'blue' },
  { key: 'products_sold', label: 'Prodotti venduti', icon: TrendingUp, format: 'int', tone: 'yellow' },
  { key: 'total_clients', label: 'Clienti totali', icon: UsersIcon, format: 'int', tone: 'violet' },
  { key: 'booked_hours', label: 'Ore prenotate', icon: Clock, format: 'hours', tone: 'amber' },
  { key: 'occupancy_pct', label: '% Occupazione', icon: TrendingUp, format: 'pct', tone: 'rose' },
];

const TONE_GRADIENTS = {
  emerald: 'from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-900',
  blue: 'from-blue-50 to-blue-100 border-blue-200 text-blue-900',
  violet: 'from-violet-50 to-violet-100 border-violet-200 text-violet-900',
  amber: 'from-amber-50 to-amber-100 border-amber-200 text-amber-900',
  rose: 'from-rose-50 to-rose-100 border-rose-200 text-rose-900',
  yellow: 'from-yellow-50 to-yellow-100 border-yellow-200 text-yellow-900',
};

const MONTH_LABELS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

function fmtValue(value, fmt) {
  if (value === null || value === undefined) return '—';
  switch (fmt) {
    case 'currency': return `€ ${Math.round(value).toLocaleString('it-IT')}`;
    case 'int': return Math.round(value).toLocaleString('it-IT');
    case 'pct': return `${value.toFixed(1)}%`;
    case 'hours': return `${value.toFixed(1)}h`;
    case 'rating': return `★ ${value.toFixed(2)}`;
    default: return String(value);
  }
}

function pctDelta(current, previous) {
  if (current == null || previous == null || previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

function greetingFor(hour) {
  if (hour < 6) return 'Buona notte';
  if (hour < 12) return 'Buongiorno';
  if (hour < 18) return 'Buon pomeriggio';
  return 'Buona sera';
}

export default function StaffHomePage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await staffApi.myDashboard();
      setData(r.data);
      setError(null);
    } catch (e) {
      if (e?.response?.status === 404) {
        setError('no_staff_linked');
      } else {
        toast.error('Errore caricamento home');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error === 'no_staff_linked') {
    return (
      <div className="max-w-2xl mx-auto bg-amber-50 border border-amber-200 rounded-sm p-10 text-center" data-testid="no-staff-linked">
        <FileWarning className="w-12 h-12 mx-auto text-amber-500 mb-3" />
        <h2 className="text-lg font-bold text-amber-900">Nessuna scheda staff collegata</h2>
        <p className="text-sm text-amber-800 mt-2 max-w-md mx-auto">
          Il tuo account non è ancora linkato a una scheda dipendente.
          Contatta il tuo Operations Manager per completare la configurazione.
        </p>
      </div>
    );
  }

  if (!data) return null;

  const { staff, performance_latest, performance_previous, open_tickets, docs_expiring, agenda, recent_achievements } = data;
  const hour = new Date().getHours();
  const firstName = staff?.first_name || '';
  const roleLabel = ROLE_LABELS[staff?.role_in_store] || staff?.role_in_store;

  return (
    <div className="space-y-6" data-testid="staff-home-page">
      {/* Hero */}
      <header className="bg-slate-900 text-white rounded-sm p-6 md:p-8 flex items-center gap-5" data-testid="staff-home-hero">
        {staff?.photo_url ? (
          <img src={staff.photo_url} alt={firstName}
               className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover ring-2 ring-white/20" />
        ) : (
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/10 flex items-center justify-center text-2xl font-bold">
            {firstName?.[0]?.toUpperCase() || '?'}
          </div>
        )}
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-widest text-slate-400">{greetingFor(hour)}</p>
          <h1 className="text-2xl md:text-3xl font-bold truncate" data-testid="staff-home-greeting">{firstName}</h1>
          <p className="text-sm text-slate-300 mt-1">
            {roleLabel}{staff?.store_name ? ` · ${staff.store_name}` : ''}
          </p>
        </div>
        <div className="ml-auto hidden md:block">
          <Link
            to="/my-profile"
            className="text-xs uppercase tracking-widest text-slate-300 hover:text-white border border-white/20 px-3 py-2 inline-flex items-center gap-1"
            data-testid="link-my-profile"
          >
            Il mio profilo <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Performance: 2/3 width on lg */}
        <section className="lg:col-span-2 space-y-3" data-testid="performance-section">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-widest text-slate-700">Le tue performance</h2>
            {performance_latest && (
              <span className="text-xs text-slate-500">
                {MONTH_LABELS[performance_latest.month - 1]} {performance_latest.year}
              </span>
            )}
          </div>
          {performance_latest ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="performance-tiles">
              {KEY_METRICS.map(m => {
                const Icon = m.icon;
                const v = performance_latest.metrics?.[m.key];
                const p = performance_previous?.metrics?.[m.key];
                const delta = pctDelta(v, p);
                return (
                  <div key={m.key}
                       className={`bg-gradient-to-br ${TONE_GRADIENTS[m.tone]} border rounded-sm p-4`}
                       data-testid={`home-metric-${m.key}`}>
                    <div className="flex items-start justify-between mb-2">
                      <Icon className="w-4 h-4 opacity-70" />
                      {delta !== null && (
                        <span className={`text-[10px] font-bold ${delta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                          {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}%
                        </span>
                      )}
                    </div>
                    <p className="text-xl md:text-2xl font-bold">{fmtValue(v, m.format)}</p>
                    <p className="text-xs opacity-80 mt-0.5">{m.label}</p>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyCard
              icon={<TrendingUp className="w-8 h-8 text-slate-300" />}
              testid="performance-empty"
              title="Nessuna performance disponibile"
              body="I dati appariranno qui dopo il prossimo import Fresha. Chiedi al tuo manager se ti aspettavi di vederli."
            />
          )}
          <div className="text-right">
            <Link
              to={`/staff/${staff.staff_id}`}
              className="text-xs text-slate-600 hover:text-slate-900 underline underline-offset-2"
              data-testid="link-staff-detail"
            >
              Vai alla mia scheda completa
            </Link>
          </div>
        </section>

        {/* Sidebar: 1/3 width on lg → Documenti + Achievement */}
        <aside className="space-y-6">
          {/* Documenti in scadenza */}
          <section data-testid="docs-section">
            <h2 className="text-sm font-bold uppercase tracking-widest text-slate-700 mb-3">
              Documenti in scadenza
            </h2>
            {docs_expiring && docs_expiring.length > 0 ? (
              <div className="space-y-2" data-testid="docs-list">
                {docs_expiring.map(d => {
                  const dayLeft = d.expires_at ? differenceInDays(parseISO(d.expires_at), new Date()) : null;
                  const tone = dayLeft != null && dayLeft <= 7
                    ? 'border-rose-200 bg-rose-50 text-rose-900'
                    : 'border-amber-200 bg-amber-50 text-amber-900';
                  return (
                    <div key={d.doc_id}
                         className={`border ${tone} rounded-sm p-3 flex items-start gap-2`}
                         data-testid={`doc-row-${d.doc_id}`}>
                      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs uppercase tracking-wider opacity-70">{DOC_TYPE_LABELS[d.doc_type] || d.doc_type}</p>
                        <p className="text-sm font-semibold truncate">{d.title || DOC_TYPE_LABELS[d.doc_type]}</p>
                        <p className="text-[11px] mt-1">
                          {dayLeft != null && dayLeft >= 0
                            ? `Scade tra ${dayLeft} giorn${dayLeft === 1 ? 'o' : 'i'}`
                            : 'Scaduto'}
                          {d.expires_at && ` · ${format(parseISO(d.expires_at), 'dd MMM yyyy', { locale: itLocale })}`}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyCard testid="docs-empty"
                         icon={<FileWarning className="w-6 h-6 text-slate-300" />}
                         title="Tutto in regola"
                         body="Nessun documento in scadenza nei prossimi 30 giorni." />
            )}
          </section>

          {/* Achievement recenti */}
          <section data-testid="achievements-section">
            <h2 className="text-sm font-bold uppercase tracking-widest text-slate-700 mb-3">
              Achievement recenti
            </h2>
            {recent_achievements && recent_achievements.length > 0 ? (
              <div className="space-y-2" data-testid="achievements-list">
                {recent_achievements.map(a => (
                  <div key={a.ach_id || `${a.ach_type}-${a.earned_at}`}
                       className="bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-200 rounded-sm p-3 flex items-center gap-3"
                       data-testid={`ach-row-${a.ach_id || a.ach_type}`}>
                    <div className="text-2xl">{a.icon || '🏆'}</div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900 truncate">{a.title}</p>
                      {a.earned_at && (
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          {format(parseISO(a.earned_at), 'dd MMM yyyy', { locale: itLocale })}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyCard testid="ach-empty"
                         icon={<Award className="w-6 h-6 text-slate-300" />}
                         title="Nessun achievement"
                         body="I traguardi e gli anniversari di servizio appariranno qui." />
            )}
          </section>
        </aside>
      </div>

      {/* Ticket aperti + Agenda settimana */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section data-testid="tickets-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold uppercase tracking-widest text-slate-700">Ticket aperti su di te</h2>
            <Link to="/assistance" className="text-xs text-slate-600 hover:text-slate-900 underline underline-offset-2"
                  data-testid="link-assistance">
              Vai all'Assistenza
            </Link>
          </div>
          {open_tickets && open_tickets.length > 0 ? (
            <div className="space-y-2" data-testid="tickets-list">
              {open_tickets.slice(0, 6).map(t => (
                <Link key={t.ticket_id} to={`/assistance/${t.ticket_id}`}
                      className="block border border-slate-200 rounded-sm p-3 hover:border-slate-400 transition-colors"
                      data-testid={`ticket-row-${t.ticket_id}`}>
                  <div className="flex items-start gap-2">
                    <Wrench className="w-4 h-4 text-slate-400 mt-1 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900 truncate">{t.title}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${PRIORITY_TONES[t.priority]}`}>
                          {PRIORITY_LABELS[t.priority]}
                        </span>
                        <span className="text-[11px] text-slate-500">
                          {STATUS_LABELS[t.status]}{t.store_name ? ` · ${t.store_name}` : ''}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 mt-1 shrink-0" />
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyCard testid="tickets-empty"
                       icon={<Wrench className="w-6 h-6 text-slate-300" />}
                       title="Nessun ticket aperto"
                       body="Quando avrai ticket assegnati o creati da te, li vedrai qui." />
          )}
        </section>

        <section data-testid="agenda-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold uppercase tracking-widest text-slate-700">La settimana del negozio</h2>
            {agenda?.week_start && (
              <span className="text-xs text-slate-500">
                {format(parseISO(agenda.week_start), 'dd MMM', { locale: itLocale })}
                {' → '}
                {format(parseISO(agenda.week_end), 'dd MMM', { locale: itLocale })}
              </span>
            )}
          </div>
          <div className="space-y-3">
            <AgendaBlock
              testid="birthdays-block"
              icon={<Cake className="w-4 h-4" />}
              title="Compleanni"
              items={agenda?.birthdays || []}
              renderItem={(p) => (
                <span key={p.staff_id}>
                  <strong>{p.first_name} {p.last_name}</strong>
                  <span className="text-slate-500"> · {p.month_day}</span>
                </span>
              )}
              tone="amber"
            />
            <AgendaBlock
              testid="anniversaries-block"
              icon={<PartyPopper className="w-4 h-4" />}
              title="Anniversari di servizio"
              items={agenda?.anniversaries || []}
              renderItem={(p) => (
                <span key={`${p.staff_id}-anniv`}>
                  <strong>{p.first_name} {p.last_name}</strong>
                  <span className="text-slate-500"> · {p.years} ann{p.years === 1 ? 'o' : 'i'} ({p.month_day})</span>
                </span>
              )}
              tone="violet"
            />
          </div>
        </section>
      </div>
    </div>
  );
}

function EmptyCard({ icon, title, body, testid }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-sm p-5 text-center" data-testid={testid}>
      <div className="flex justify-center mb-2">{icon}</div>
      <p className="text-sm font-semibold text-slate-700">{title}</p>
      <p className="text-xs text-slate-500 mt-1">{body}</p>
    </div>
  );
}

function AgendaBlock({ icon, title, items, renderItem, tone, testid }) {
  const toneClasses = {
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
    violet: 'border-violet-200 bg-violet-50 text-violet-900',
  }[tone] || 'border-slate-200 bg-slate-50 text-slate-900';

  if (!items || items.length === 0) {
    return (
      <div className="border border-slate-200 rounded-sm p-3 text-slate-500 text-sm flex items-center gap-2" data-testid={testid}>
        <span className="text-slate-300">{icon}</span>
        <span className="font-medium">{title}</span>
        <span className="text-xs text-slate-400">— niente questa settimana</span>
      </div>
    );
  }
  return (
    <div className={`border rounded-sm p-3 ${toneClasses}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-xs font-bold uppercase tracking-wider">{title}</span>
      </div>
      <ul className="space-y-1 text-sm">
        {items.map((p, i) => <li key={p.staff_id + '-' + i}>{renderItem(p)}</li>)}
      </ul>
    </div>
  );
}
